import * as _ from "lodash-es";

import "./polyfill/urlSearchParams";
import "./polyfill/fetch";
import { addUrlParams } from "@core/utils/link";

const FETCH_FAILED = "FETCH_FAILED";
const FETCH_TIMEOUT = "FETCH_TIMEOUT";
const FETCH_ABORT = "FETCH_ABORT";
const DATA_SERIALIZATION_FAIL = "DATA_SERIALIZATION_FAIL";
const FETCH_UNAUTHORIZED = "FETCH_UNAUTHORIZED";

export const ERROR_CODE = {
  FETCH_FAILED,
  FETCH_TIMEOUT,
  FETCH_ABORT,
  DATA_SERIALIZATION_FAIL,
  FETCH_UNAUTHORIZED,
};
// 以 fetch 为基础，打造不需要 jquery 的 ajax 系统
// 默认设置
const getDefaultHeaders = () => {
  const headers = {
    Accept: "application/json, text/javascript, */*",
  };
  
  // Add Authorization header if token exists in localStorage
  try {
    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  
  return headers;
};
const defaultOptions = {
  method: "POST",
  credentials: "include",
  cache: "no-cache",
};

const ajaxQueue = {};
const ajaxQueueExecuting = {};

const queueExecute = (queueKey) => {
  let _queue = ajaxQueue[queueKey];
  // console.log('queueExecute', ajaxQueueExecuting[queueKey], _queue);
  if (_queue && _queue.length > 0 && !ajaxQueueExecuting[queueKey]) {
    // 有元素，且没有在执行
    _queue.shift()();
  }
};

export default function (url, options) {
  let { data, timeout, mode, queueKey, dataType, multipart, headers, ...opt } =
    options;
  mode = mode || "abort";
  queueKey =
    queueKey ||
    `${url}_${dataType || "default"}_${multipart || "default"}_${JSON.stringify(
      data || null
    )}`;
  // 扩展生成 fetch-options
  let fetchOptions = _.assign({}, defaultOptions, { headers: getDefaultHeaders() }, opt);
  // 增加 data 相关的处理
  let _d = data;
  if (multipart) {
    // -- 有图片的情况
    _d = new FormData();
    _.forEach(data, (value, key) => {
      if (_.isArray(value)) {
        _.forEach(value, (v) => {
          _d.append(`${key}[]`, v);
        });
      } else {
        _d.append(key, value);
      }
    });
  } else {
    _d = new URLSearchParams();
    _.forEach(data, (value, key) => {
      _d.set(key, value);
    });
  }

  if (fetchOptions.method === "GET" || fetchOptions.method === "HEAD") {
    if (!multipart) {
      url = addUrlParams(url, _d);
    }
  } else {
    fetchOptions.body = _d;
  }

  fetchOptions.headers = _.assign({}, fetchOptions.headers, headers);

  // 增加 timeout & abort 相关的支持

  let controller, abort;
  let fetcher;
  if (AbortController) {
    controller = new AbortController();
    fetchOptions.signal = controller.signal;
    abort = () => {
      controller.abort();
    };
    fetcher = () => fetch(url, fetchOptions);
  } else {
    const abortPromise = new Promise((resolve, reject) => {
      abort = (res = ERROR_CODE.FETCH_ABORT) => {
        reject(res);
      };
    });
    fetcher = () => Promise.race([fetch(url, fetchOptions), abortPromise]);
  }

  const execute = () => {
    // 开始计时相关的处理
    let _timeoutId = null;
    if (timeout) {
      // handler 超时处理
      _timeoutId = setTimeout(() => abort(), timeout);
    }
    const p = fetcher()
      .then(function (response) {
        clearTimeout(_timeoutId);
        // Handle 401 Unauthorized - redirect to login
        if (response.status === 401) {
          // Clear stored auth data
          try {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('username');
          } catch (e) {
            // Ignore localStorage errors
          }
          // Show authorization error and redirect
          import('antd').then(({ message }) => {
            message.error('Authorization Error: Your session has expired. Please login again.');
          }).catch(() => {
            alert('Authorization Error: Your session has expired. Please login again.');
          });
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
          return { error: ERROR_CODE.FETCH_UNAUTHORIZED };
        }
        if (response.ok) {
          let dataPromise = null;
          let error = null;

          let serializationResponse = response.clone();
          const serializationError = () => {
            error = ERROR_CODE.DATA_SERIALIZATION_FAIL;
            return serializationResponse.text();
          };

          if (_.isArray(dataType)) {
            _.forEach(dataType, (_dataType) => {
              switch (_dataType) {
                case "text":
                  if (!dataPromise) {
                    dataPromise = response.text();
                  } else {
                    let _textSerializationResponse =
                      serializationResponse.clone();
                    dataPromise = dataPromise.catch(() => {
                      return _textSerializationResponse.text();
                    });
                  }
                  break;
                case "blob":
                  if (!dataPromise) {
                    dataPromise = response.blob();
                  } else {
                    let _bolbSerializationResponse =
                      serializationResponse.clone();
                    dataPromise = dataPromise.catch(() => {
                      return _bolbSerializationResponse.blob();
                    });
                  }
                  break;
                case "json":
                default:
                  if (!dataPromise) {
                    dataPromise = response.json();
                  } else {
                    let _jsonSerializationResponse =
                      serializationResponse.clone();
                    dataPromise = dataPromise.catch(() => {
                      return _jsonSerializationResponse.json();
                    });
                  }
                  break;
              }
            });
            dataPromise = dataPromise.catch(serializationError);
          } else {
            switch (dataType) {
              case "text":
                dataPromise = response.text();
                break;
              case "blob":
                dataPromise = response.blob().catch(serializationError);
                break;
              case "json":
              default:
                dataPromise = response.json().catch(serializationError);
                break;
            }
          }

          return dataPromise.then((res) => {
            serializationResponse = null;
            return { error: error, res: res, response };
          });
        }
        return { error: ERROR_CODE.FETCH_FAILED };
      })
      .catch(function (err) {
        clearTimeout(_timeoutId);
        if (err && err.name === "AbortError") {
          return { error: ERROR_CODE.FETCH_ABORT };
        }
        return { error: err };
      })
      .then((res) => {
        return res;
      });

    return p;
  };

  let ajaxPromise = null;

  if (mode === "queue") {
    // 自己去控制状态的变化，增加需要的回调
    if (ajaxQueue[queueKey]) {
      if (!Array.isArray(ajaxQueue[queueKey])) {
        if (ajaxQueue[queueKey] && ajaxQueue[queueKey].abort) {
          ajaxQueue[queueKey].abort();
        }
        ajaxQueue[queueKey] = null;
      }
    }
    if (!ajaxQueue[queueKey]) {
      ajaxQueue[queueKey] = [];
    }
    ajaxPromise = new Promise((resolve) => {
      ajaxQueue[queueKey].push(function queueExecuteItem() {
        ajaxQueueExecuting[queueKey] = true;
        execute().then((res) => {
          resolve(res);
          ajaxQueueExecuting[queueKey] = false;
          queueExecute(queueKey); // 传递 key，进行下一个队列对象的处理
        });
      });
    });
    // 开始触发
    queueExecute(queueKey);
  } else if (mode === "abort") {
    // 处理只接受最后一次请求
    if (ajaxQueue[queueKey] && ajaxQueue[queueKey].abort) {
      ajaxQueue[queueKey].abort();
      ajaxQueue[queueKey] = null;
    }
    ajaxPromise = execute().then((res) => {
      ajaxQueue[queueKey] = null;
      return res;
    });
    ajaxQueue[queueKey] = ajaxPromise;
  } else if (mode === "cancel") {
    if (ajaxQueue[queueKey]) {
      ajaxPromise = new Promise((resolve, reject) => {
        resolve({ error: ERROR_CODE.FETCH_ABORT });
      });
    } else {
      ajaxPromise = execute().then((res) => {
        ajaxQueue[queueKey] = null;
        return res;
      });
      ajaxQueue[queueKey] = ajaxPromise;
    }
  } else {
    ajaxPromise = execute();
  }

  // 增加 abort
  ajaxPromise.abort = abort;
  // 永远返回 resolve 的状态
  return ajaxPromise;
}
